import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container } from '@mui/material';
import { selectClassroom } from 'state/modules/app/actions';
import { getSelectedClassroom } from 'state/modules/app/selectors';
import { getAssignments } from 'state/modules/entity/thunks/assignment';
import { fetchUserClassrooms } from 'state/modules/entity/custom/classrooms/thunks';
import { AppStoreState } from 'types';
import { parseQueryParams, buildSearchQueryParams } from 'util/query-params-util';
import { getNewLink } from 'util/workbook-util';
import ClassroomAssignmentList from './ClassroomAssignmentList';
import Filters, { AssignmentFilters } from './Filters';
import { StudentAssignmentList } from './StudentAssignmentList';

const parseFilterQuery = (search: string): AssignmentFilters => {
  const queryParams = parseQueryParams(search);
  const filter: AssignmentFilters = { classroomId: null, studentId: null };

  if (queryParams?.classroomId) filter.classroomId = Number(queryParams.classroomId);
  if (queryParams?.studentId) filter.studentId = Number(queryParams.studentId);

  return filter;
};

export const AssignmentList: React.FC = () => {
  const dispatch = useDispatch() as any;
  const location = useLocation();
  const navigate = useNavigate();

  const classrooms = useSelector(({ entity }: AppStoreState) => entity.classrooms) ?? [];
  const selectedClassroom = useSelector(getSelectedClassroom);
  const { classroomId, studentId } = parseFilterQuery(location.search);

  useEffect(() => {
    dispatch(fetchUserClassrooms());
  }, [dispatch]);

  useEffect(() => {
    if (classroomId) return;
    if (selectedClassroom) {
      navigate({ search: buildSearchQueryParams({ classroomId: selectedClassroom.id }) });
    } else if (classrooms.length > 0) {
      dispatch(selectClassroom(classrooms[0].id));
    }
  }, [dispatch, classrooms, classroomId, selectedClassroom]);

  useEffect(() => {
    if (!classroomId) return;
    const params = {
      classroomIds: [classroomId],
      ...(studentId && { includeAttempts: true, studentIds: [studentId] })
    };
    dispatch(getAssignments(params));
  }, [dispatch, classroomId, studentId]);

  const handleFilterChange = updatedFilter => {
    if (updatedFilter.classroomId !== classroomId) {
      dispatch(selectClassroom(updatedFilter.classroomId));
    }
    navigate({ search: buildSearchQueryParams(updatedFilter) });
  };

  const currentWorkbook: any = useSelector(({ app }: AppStoreState) => app.currentWorkbook);
  const assignments = useSelector(({ entity }: AppStoreState) => entity.assignments) ?? [];

  const handleAddAssignmentClick = () => {
    navigate(getNewLink(currentWorkbook?.bookCode, currentWorkbook?.edition, 'lessonPlanner'));
  };

  return (
    <Container maxWidth="xl" disableGutters>
      <Filters
        classroomId={classroomId}
        studentId={studentId}
        onFilterChange={handleFilterChange}
      />
      {studentId ? (
        <StudentAssignmentList
          assignments={assignments}
          filters={{ classroomId, studentId }}
          onAddAssignmentClick={handleAddAssignmentClick}
        />
      ) : (
        <ClassroomAssignmentList
          assignments={assignments}
          filters={{ classroomId, studentId }}
          onAddAssignmentClick={handleAddAssignmentClick}
        />
      )}
    </Container>
  );
};
